import {Helmet} from '@dr.pogodin/react-helmet';
import {FlagProps, Parameter} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {always, defaultTo, isNil} from 'ramda';
import {concatAll, isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetDocumentContextCountQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useGetDeliveryNoteQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import {featureFlags} from '@dms/feature-flags';
import {useRefreshDocumentsCount} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  Checklists,
  createHeaderActions,
  DetailTemplate,
  DetailTemplateHeader,
  EntityHeaderAction,
  getUserName,
  handleApiError,
  NavigationItem,
  Section,
  useChecklist,
  usePermissions,
} from '@dms/shared';
import {createHeaderFlags} from '@dms/warehouse/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {
  buildArray,
  composePath,
  EMPTY_PLACEHOLDER,
  generateHashFromObjects,
  Nullish,
  parseDate,
  useNavigate,
} from 'shared/web';

import {Documents} from './(sections)/Documents/Documents';
import {Overview} from './(sections)/Overview/Overview';

// Refetch the delivery note once per minute while it is still being processed by the backend.
const PROCESSING_POLLING_INTERVAL = 60 * 1000;

// eslint-disable-next-line max-statements -- Pre-existing violation
export function DeliveryNoteDetail() {
  const {deliveryNoteId} = useWarehouseParams();

  const navigate = useNavigate();

  const dateTimeFormatter = useDateTimeFormatter();

  const [pollingInterval, setPollingInterval] = useState(0);

  const [canEditDeliveryNote, canReadReceiveNote] = usePermissions({
    permissionKeys: ['deliveryNoteEdit', 'receiveNoteRead'],
  });

  const {
    data: deliveryNote,
    isLoading: isDeliveryNoteLoading,
    isFetching: isDeliveryNoteFetching,
    isError: isDeliveryNoteError,
  } = useGetDeliveryNoteQuery(
    {deliveryNoteId},
    {skip: isNilOrEmpty(deliveryNoteId), pollingInterval}
  );

  const {
    data: deliveryNoteActions,
    isLoading: isDeliveryNoteActionsLoading,
    isError: isDeliveryNoteActionsError,
  } = useGetWarehouseHeaderDynamicActionsQuery(
    {id: deliveryNoteId, resource: 'delivery-note'},
    {skip: isNilOrEmpty(deliveryNoteId)}
  );

  const {data: createdByUser, isLoading: isCreatedByUserLoading} = useGetUserQuery(
    // eslint-disable-next-line typescript/no-non-null-asserted-optional-chain -- Muted pre-existing violation
    {id: deliveryNote?.createdBy!},
    {skip: isNil(deliveryNote?.createdBy)}
  );

  const {data: updatedByUser, isLoading: isUpdatedByUserLoading} = useGetUserQuery(
    // eslint-disable-next-line typescript/no-non-null-asserted-optional-chain -- Muted pre-existing violation
    {id: deliveryNote?.updatedBy!},
    {skip: isNil(deliveryNote?.updatedBy)}
  );

  const documentCountQuery = useGetDocumentContextCountQuery(
    {contextTarget: 'warehouse-delivery-note', contextId: deliveryNoteId},
    {skip: isNilOrEmpty(deliveryNoteId)}
  );

  const [putDeliveryNoteAction, {isLoading: isPutDeliveryNoteActionLoading}] =
    usePutWarehouseHeaderDynamicActionsMutation();

  const refreshDocumentsCount = useRefreshDocumentsCount({
    contextTarget: 'warehouse-delivery-note',
    contextId: deliveryNoteId,
  });

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: deliveryNoteId,
    resourceId: 'DELIVERY_NOTE',
  });

  const isLoading =
    isDeliveryNoteLoading ||
    isDeliveryNoteActionsLoading ||
    isCreatedByUserLoading ||
    isUpdatedByUserLoading;

  const hasError = isDeliveryNoteError || isDeliveryNoteActionsError;

  const isDraftDeliveryNote = isNil(deliveryNote);
  const isProcessingDeliveryNote = deliveryNote?.state === 'PROCESSING';
  const isPendingDeliveryNote = deliveryNote?.state === 'PENDING';

  // Drive the polling interval from the loaded state — RTK Query re-reads it on every render.
  const nextPollingInterval = isProcessingDeliveryNote ? PROCESSING_POLLING_INTERVAL : 0;
  if (pollingInterval !== nextPollingInterval) {
    setPollingInterval(nextPollingInterval);
  }

  const isReceivedDeliveryNote = deliveryNote?.state === 'RECEIVED';
  const isCompletedDeliveryNote = deliveryNote?.state === 'COMPLETED';

  const formatToShortDate = (date: string | Nullish) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return dateTimeFormatter('dateShort', parseDate(date));
  };

  const createdBy = `${i18n.t('general.labels.createdBy')}: ${getUserName(createdByUser) ?? EMPTY_PLACEHOLDER}`;
  const createdAt = `${i18n.t('general.labels.createdAt')}: ${formatToShortDate(deliveryNote?.createdAt)}`;

  const updatedBy = `${i18n.t('general.labels.updatedBy')}: ${getUserName(updatedByUser) ?? EMPTY_PLACEHOLDER}`;
  const updatedAt = `${i18n.t('general.labels.updatedAt')}: ${formatToShortDate(deliveryNote?.updatedAt)}`;

  const completedBy = `${i18n.t('general.labels.completedBy')}: ${isCompletedDeliveryNote ? getUserName(updatedByUser) : EMPTY_PLACEHOLDER}`;
  const completedAt = `${i18n.t('general.labels.completedAt')}: ${isCompletedDeliveryNote ? formatToShortDate(deliveryNote?.updatedAt) : EMPTY_PLACEHOLDER}`;

  const headerTitle = `${i18n.t('entity.warehouse.labels.deliveryNote')} ${defaultTo(
    '',
    deliveryNote?.deliveryNoteNumber
  )}`;

  const headerParameters = match(isDraftDeliveryNote)
    .returnType<Parameter[]>()
    .with(true, always([]))
    .otherwise(always([createdBy, createdAt, updatedBy, updatedAt, completedBy, completedAt]));

  const markersFlags = createHeaderFlags(deliveryNote?.markers);

  const staticFlags = buildArray<FlagProps>()
    .when(isProcessingDeliveryNote, {
      colorScheme: 'neutral',
      label: i18n.t('general.labels.processing'),
    })
    .when(isPendingDeliveryNote, {colorScheme: 'blue', label: i18n.t('general.labels.pending')})
    .when(isReceivedDeliveryNote, {colorScheme: 'blue', label: i18n.t('general.labels.received')})
    .when(isCompletedDeliveryNote, {
      colorScheme: 'green',
      label: i18n.t('general.labels.completed'),
    })
    .when(deliveryNote?.type === 'CORE_EXCHANGE', {
      colorScheme: 'purple',
      label: i18n.t('entity.warehouse.labels.coreExchange'),
    });

  const flags = concatAll<FlagProps[]>([staticFlags, markersFlags]);

  const handleActionClick = (actionKey: string) => {
    putDeliveryNoteAction({id: deliveryNoteId, resource: 'delivery-note', body: {actionKey}})
      .unwrap()
      .catch(handleApiError);
  };

  const hasReceiveNoteId = isNotNilOrEmpty(deliveryNote?.receiveNote?.receiveNoteId);

  const navigateToReceiveNoteDetail = () => {
    if (!hasReceiveNoteId) {
      throw new Error('Missing receiveNoteId');
    }

    navigate(
      composePath(warehouseRoutes.receiveNoteDetailOverview, {
        params: {id: deliveryNote?.receiveNote?.receiveNoteId as string},
        isSearchParamsPreserved: false,
      })
    );
  };

  const staticActions = buildArray<EntityHeaderAction>().when(
    hasReceiveNoteId && canReadReceiveNote,
    {
      type: 'button',
      title: i18n.t('entity.warehouse.actions.goToReceiptNote'),
      onClick: navigateToReceiveNoteDetail,
    }
  );

  const headerActions = createHeaderActions({
    actions: deliveryNoteActions?.actions,
    callback: handleActionClick,
    isLoading: isPutDeliveryNoteActionLoading,
  });

  const header: DetailTemplateHeader = {
    title: headerTitle,
    isEntityLoading: isDeliveryNoteFetching,
    icon: 'action/deployed',
    parameters: headerParameters,
    recordId: deliveryNote?.deliveryNoteId,
    resourceId: EntityResourceIds.deliveryNote,
    flags,
    actions: [...headerActions, ...staticActions],
    controls: ['ASSIGNEE'],
    alerts: !canEditDeliveryNote
      ? [
          {
            type: 'banner',
            variant: 'info',
            message: i18n.t('module.warehouse.label.missingDeliveryNoteEditPermission'),
          },
        ]
      : undefined,
  };

  const navigation = buildArray<NavigationItem>([
    {
      id: 'deliveryNoteDetail',
      label: i18n.t('page.warehouse.labels.overview'),
      href: composePath(warehouseRoutes.deliveryNoteDetailOverview, {
        params: {id: deliveryNoteId},
      }),
      content: (
        <Overview
          deliveryNote={deliveryNote}
          isLoading={isDeliveryNoteLoading}
          hasError={isDeliveryNoteError}
          data-testid={testIds.warehouse.deliveryNoteDetailOverview('sections.overview')}
        />
      ),
      'data-testid': testIds.warehouse.deliveryNoteDetailOverview('navigation.overview'),
    },
  ])
    .add({
      id: 'deliveryNoteDocuments',
      label: i18n.t('page.warehouse.labels.documents'),
      href: composePath(warehouseRoutes.deliveryNoteDetailDocuments, {
        params: {id: deliveryNoteId},
      }),
      content: (
        <Documents
          onDocumentAction={refreshDocumentsCount}
          data-testid={testIds.warehouse.deliveryNoteDetailDocuments('sections.documents')}
        />
      ),
      badge: documentCountQuery.data?.count
        ? {value: documentCountQuery.data.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.warehouse.deliveryNoteDetailDocuments('navigation.documents'),
    })
    .whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, () => ({
      id: 'deliveryNoteChecklists',
      label: i18n.t('entity.checklist.labels.title'),
      href: composePath(warehouseRoutes.deliveryNoteDetailChecklists, {
        params: {id: deliveryNoteId},
      }),
      content: (
        <Section>
          <Checklists
            {...checklistSettings}
            contextTarget="warehouse-delivery-note"
            contextId={deliveryNoteId}
          />
        </Section>
      ),
      badge: badgeSettings,
      'data-testid': testIds.warehouse.deliveryNoteDetailChecklists('checklists'),
    }));

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.deliveryNote')} />
      <DetailTemplate
        key={generateHashFromObjects(deliveryNote)}
        isLoading={isLoading}
        isError={hasError}
        isLocked={isPutDeliveryNoteActionLoading}
        header={header}
        navigation={navigation}
        data-testid={testIds.warehouse.deliveryNoteDetailOverview('page')}
      />
    </>
  );
}
